import { educationLevelMap } from '@/components/patients/constants';
import { SECTIONS } from '@/components/wizard/constants';
import { EMPTY_VALUE } from '@/constants';
import { formatDateOnly } from '@/lib/date';
import { getAge } from '@/utils';
import { translateGender } from '@/utils/translateGender';
import type { Psychologist, ReportData } from '../types';

interface StandalonePatientInfoTableProps {
    data: ReportData;
    psychologist?: Psychologist | null;
}

export default function StandalonePatientInfoTable({
    data,
    psychologist,
}: StandalonePatientInfoTableProps) {
    const { patient } = data;
    const educationLevelValue = patient?.education_level
        ? (educationLevelMap[patient.education_level] ?? patient.education_level)
        : EMPTY_VALUE;

    const psychologistParts: string[] = [];

    if (psychologist?.full_name) psychologistParts.push(psychologist.full_name);
    if (psychologist?.crp) psychologistParts.push(`CRP ${psychologist.crp}`);
    if (psychologist?.email) psychologistParts.push(psychologist.email);

    return (
        <div id={`section-${SECTIONS.PATIENT}`} className="mb-6">
            <table className="w-full border-collapse border border-slate-300 bg-white text-xs">
                <tbody>
                    <tr>
                        <td className="w-1/6 border border-slate-300 bg-slate-50 p-2 font-bold">
                            Nome:
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700">
                            {patient?.full_name || EMPTY_VALUE}
                        </td>
                        <td className="w-1/6 border border-slate-300 bg-slate-50 p-2 font-bold">
                            Sexo:
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700">
                            {translateGender(patient?.gender)}
                        </td>
                        <td className="w-1/6 border border-slate-300 bg-slate-50 p-2 font-bold">
                            Idade:
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700">
                            {getAge(patient?.date_of_birth) || EMPTY_VALUE}
                        </td>
                    </tr>

                    <tr>
                        <td className="border border-slate-300 bg-slate-50 p-2 font-bold">
                            Data Nasc:
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700">
                            {patient?.date_of_birth
                                ? formatDateOnly(patient.date_of_birth)
                                : EMPTY_VALUE}
                        </td>
                        <td
                            className="border border-slate-300 bg-slate-50 p-2 font-bold"
                            colSpan={2}
                        >
                            Escolaridade:
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700" colSpan={2}>
                            {educationLevelValue || EMPTY_VALUE}
                        </td>
                    </tr>

                    <tr>
                        <td className="border border-slate-300 bg-slate-50 p-2 font-bold">
                            Solicitante:
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700" colSpan={5}>
                            {patient?.referring_professional || EMPTY_VALUE}
                        </td>
                    </tr>

                    <tr>
                        <td className="border border-slate-300 bg-slate-50 p-2 font-bold">
                            Finalidade:
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700" colSpan={5}>
                            {data.finalidade || EMPTY_VALUE}
                        </td>
                    </tr>

                    <tr>
                        <td className="border border-slate-300 bg-slate-50 p-2 font-bold">
                            Autor(a)
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700" colSpan={5}>
                            {psychologistParts.length > 0
                                ? psychologistParts.join(' | ')
                                : EMPTY_VALUE}
                        </td>
                    </tr>

                    <tr>
                        <td className="border border-slate-300 bg-slate-50 p-2 font-bold">
                            Período de Avaliação
                        </td>
                        <td className="border border-slate-300 p-2 text-slate-700" colSpan={5}>
                            <div className="flex items-center gap-3">
                                <span>
                                    {data.tests_performed_at_start
                                        ? formatDateOnly(data.tests_performed_at_start)
                                        : EMPTY_VALUE}
                                </span>
                                <span className="shrink-0 text-center">até</span>
                                <span>
                                    {data.tests_performed_at_end
                                        ? formatDateOnly(data.tests_performed_at_end)
                                        : EMPTY_VALUE}
                                </span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
